import { difference } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { requireCompanyPreferences } from '~/data/companyPreferences/slice';
import { useCompanyPreferencesBooleanFlagSelector } from '~/data/companyPreferences/useCompanyPreferencesBooleanFlagSelector';
import { StoreState } from '~/reducer';
import { companyPreferencesStateSelector } from '~/selectors';

export interface UseLoadRemoteCompanyPreferencesOutput {
  isLoading: boolean;
  failedToLoad: boolean;
}

export const useLoadRemoteCompanyPreferences = (
  keys?: string[],
): UseLoadRemoteCompanyPreferencesOutput => {
  const dispatch = useDispatch();

  const isLoading = useCompanyPreferencesBooleanFlagSelector('isLoading', keys);
  const failedToLoad = useCompanyPreferencesBooleanFlagSelector(
    'failedToLoad',
    keys,
  );

  const requestedCompanyPreferences = useSelector((state: StoreState) => {
    return companyPreferencesStateSelector(state).requestedCompanyPreferences;
  });

  const didNotRequestCompanyPreferences = useMemo(() => {
    return keys && requestedCompanyPreferences
      ? !!difference(keys, requestedCompanyPreferences).length
      : !keys && !!requestedCompanyPreferences;
  }, [requestedCompanyPreferences, keys]);

  useEffect(() => {
    dispatch(requireCompanyPreferences(keys));
  }, [dispatch, keys]);

  return {
    /**
     * To avoid passing isLoading false and after that true,
     * consider isLoading true while company preferences were
     * not requested.
     */
    isLoading: isLoading || didNotRequestCompanyPreferences,
    failedToLoad,
  };
};
